import { Label, Switch, Textarea } from "@f2e/ui/components/base"
import { useTranslation } from "react-i18next"

import { resolveLocalizedText } from "../registry"
import type {
  AgentTemplateHumanHandoffFieldDefinition,
  AgentTemplateToolValue,
} from "../types"
import { ConfigurableFieldShell } from "./ConfigurableFieldShell"
import type { TemplateItemRendererProps } from "./TemplateItemRenderer"

export const HumanHandoffItemRenderer = ({
  item,
  locale,
  value,
  error,
  disabled,
  onChange,
}: TemplateItemRendererProps) => {
  const { t } = useTranslation()
  if (item.type !== "tool_human_handoff") return null
  const definition = item as AgentTemplateHumanHandoffFieldDefinition
  const current = (value as AgentTemplateToolValue | undefined) ?? {
    enabled: Boolean(definition.required || definition.enabledByDefault),
    configuration: {},
  }
  const configuration = current.configuration
  const alwaysOn = configuration.alwaysOn === true
  const update = (patch: Record<string, unknown>) =>
    onChange({
      enabled: current.enabled,
      configuration: { ...configuration, ...patch },
    })
  return (
    <ConfigurableFieldShell
      id={`template-${definition.key}`}
      title={resolveLocalizedText(definition.label, locale)}
      description={
        definition.description
          ? resolveLocalizedText(definition.description, locale)
          : undefined
      }
      enabled={current.enabled}
      required={definition.required}
      disabled={disabled}
      error={error}
      onEnabledChange={(enabled) => onChange({ enabled, configuration })}
    >
      <div className="space-y-4 border-t border-border pt-4">
        <div className="space-y-2">
          <Label htmlFor={`template-${definition.key}-request`}>
            {t("workspace.agent.templates.humanHandoff.requestLabel")}
            {!alwaysOn && <span aria-hidden="true"> *</span>}
          </Label>
          <Textarea
            id={`template-${definition.key}-request`}
            aria-required={!alwaysOn}
            rows={4}
            value={String(configuration.request || "")}
            maxLength={definition.requestMaxLength}
            disabled={disabled || alwaysOn}
            placeholder={t(
              "workspace.agent.templates.humanHandoff.requestPlaceholder"
            )}
            onChange={(event) => update({ request: event.target.value })}
          />
          <p className="text-sm text-muted-foreground">
            {t("workspace.agent.templates.humanHandoff.requestHelp")}
          </p>
        </div>
        <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
          <div className="space-y-1">
            <Label htmlFor={`template-${definition.key}-always-on`}>
              {t("workspace.agent.templates.humanHandoff.alwaysOnLabel")}
            </Label>
            <p className="text-sm text-muted-foreground">
              {t("workspace.agent.templates.humanHandoff.alwaysOnHelp")}
            </p>
          </div>
          <Switch
            id={`template-${definition.key}-always-on`}
            checked={alwaysOn}
            disabled={disabled}
            onCheckedChange={(checked) => update({ alwaysOn: checked })}
          />
        </div>
      </div>
    </ConfigurableFieldShell>
  )
}
